import {
  LeftArrowLine,
} from "../components/icons/Icons";
import { useEffect, useState, useMemo } from "react";
import StepTabs from "../components/signup/Tabs";
import { useParams, Navigate, Link, useSearchParams } from "react-router-dom";
import { useGetSubscriptionPlanQuery, useLazyCheckDomainAvailabilityQuery, useCalculatePriceMutation, useValidateCouponMutation } from "../services/signup/subscriptionPlan";
import { useLazyCheckEmailAvailabilityQuery } from "../services/signup/registration";
import { CUSTOM_PLAN, COUNTRY_OPTIONS, COUNTRY_TIMEZONE_MAP } from "../constants/landingPage";
import LoadingIndicator from "../components/LoadingIndicator";
import BasketStep from "../components/signup/BasketStep";
import SignUpStep from "../components/signup/SignUpStep";
import PaymentStep from "../components/signup/PaymentStep";
import { Formik, Form } from "formik";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { usePostRegistrationSubmitMutation, useGetRegistrationQuery, useVerifyInviteTokenQuery } from "../services/signup/registration";
import PaymentPollingScreen from "../components/signup/PaymentPollingScreen";
import RequestDemoModal from "../components/Modals/RequestDemoModal";
import { toast } from "react-toastify";

import FieldAvailabilityWatcher from "../components/signup/FieldAvailabilityWatcher";
import { useGetLocationDataQuery } from "../services/public/location";
import { useCurrency } from "../hooks/useCurrency";

const Signup = () => {
  const [postRegistrationSubmit, { isLoading: isPosting }] = usePostRegistrationSubmitMutation();
  const { planSlug, inviteToken } = useParams();
  const [currentTab, setCurrentTab] = useState(0);
  const [currentStep, setCurrentStep] = useState(1);  // for registration
  const [domainStatus, setDomainStatus] = useState("idle"); // idle, checking, available, taken
  const [emailStatus, setEmailStatus] = useState("idle");   // idle, checking, available, taken

  // basket step states
  const [quantity, setQuantity] = useState(0);
  const [couponCode, setCouponCode] = useState("");

  const [triggerCheckDomain] = useLazyCheckDomainAvailabilityQuery();
  const [triggerCheckEmail] = useLazyCheckEmailAvailabilityQuery();
  const [calculatePrice, { isLoading: isCalculatingPrice }] = useCalculatePriceMutation();
  const [dynamicPricing, setDynamicPricing] = useState(null);
  const [validateCoupon] = useValidateCouponMutation();
  const [couponPricing, setCouponPricing] = useState(null);

  const { data: locationData } = useGetLocationDataQuery();
  const { convertAndFormat, isLoading: isCurrencyLoading } = useCurrency();
  
  const { t, i18n } = useTranslation('signup');

  useEffect(() => {
    if (i18n.language !== "ar") {
      i18n.changeLanguage("ar");
    }
  }, [i18n]);

  // Verify invite token if present
  const { data: inviteTokenData, isLoading: isLoadingInvite, isError: isErrorInvite, error: inviteTokenError } = useVerifyInviteTokenQuery(inviteToken, {
    skip: !inviteToken,
  });

  // subscription plan api
  const {
    data: subscriptionPlan,
    isLoading,
    isError,
  } = useGetSubscriptionPlanQuery(planSlug, { skip: !planSlug, refetchOnMountOrArgChange: true });
  
  // Demo modal
  const [openDemoModal, setOpenDemoModal] = useState(false);
  const [registrationToken, setRegistrationToken] = useState(null);
  const [paymentAction, setPaymentAction] = useState(null);
  
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get("token");
  const [showRetryValues, setShowRetryValues] = useState(!!tokenFromUrl);
  const { data: registrationData, isLoading: isLoadingRegistration, isError: isErrorRegistration } = useGetRegistrationQuery(tokenFromUrl, {
    skip: !tokenFromUrl,
  });

  // Handle invite token success/failure
  useEffect(() => {
    if (inviteToken) {
      if (inviteTokenData?.success && !registrationData?.token) {
        setCurrentTab(1);
      } else if (isErrorInvite || (inviteTokenData && !inviteTokenData.success)) {
        toast.error(inviteTokenError?.data?.error || t('error'));
      }
    }
  }, [inviteToken, inviteTokenData, isErrorInvite, t]);

  // set quantity to max employees when subscription plan is loaded
  useEffect(() => {
    // If resuming an existing session, use the session's seat count, otherwise use plan's default
    if (registrationData?.seat_count) {
      setQuantity(registrationData.seat_count);
    } else if (inviteTokenData?.data?.seat_count) {
      setQuantity(inviteTokenData.data.seat_count);
    } else if (subscriptionPlan?.plan_type === CUSTOM_PLAN) {
      setQuantity(subscriptionPlan?.min_seats);
    } else {
      setQuantity(subscriptionPlan?.max_seats);
    }
  }, [subscriptionPlan, registrationData, inviteTokenData]);

  // Resume registration session from URL token
  useEffect(() => {
    if (registrationData?.token) {
      setRegistrationToken(registrationData.token);
      if (registrationData.current_step === "completed" || registrationData.current_step === "branding") {
        setCurrentTab(2);
      } else if (registrationData.current_step === "auth" || registrationData.is_invite_token) {
        if (currentTab === 0) setCurrentTab(1);
      }
      if (registrationData.coupon_code) {
        setCouponCode(registrationData.coupon_code);
        handleApplyCoupon(registrationData.coupon_code, registrationData.seat_count);
      }
    }
  }, [registrationData, subscriptionPlan]);

  // Fetch dynamic pricing for custom plans when quantity changes
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (subscriptionPlan?.id && quantity > 0 && subscriptionPlan?.plan_type === CUSTOM_PLAN) {
        calculatePrice({ plan_id: subscriptionPlan.id, seat_count: quantity })
          .unwrap()
          .then((res) => setDynamicPricing(res))
          .catch((err) => console.error("Failed to calculate price", err));
      } else {
        setDynamicPricing(null);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [quantity, subscriptionPlan, calculatePrice]);

  useEffect(() => {
    setCouponPricing(null);
    setCouponCode("");
  }, [quantity, subscriptionPlan]);

  const handleApplyCoupon = async (code, manualQuantity = null) => {
    if (!code) {
      setCouponPricing(null);
      return;
    }
    try {
      setShowRetryValues(false);
      if (!subscriptionPlan?.id) return;
      const seat_count = manualQuantity ?? quantity;
      if (!seat_count || seat_count <= 0) return;

      const res = await validateCoupon({
        coupon_code: code,
        plan_id: subscriptionPlan.id,
        seat_count: seat_count
      }).unwrap();

      if (res.success) {
        setCouponPricing(res.data);
        toast.success(t('coupon_applied', 'Coupon applied successfully!'));
      } else {
        setCouponPricing(null);
        toast.error(res.error || t('invalid_coupon', 'Invalid coupon code'));
      }
    } catch (err) {
      setCouponPricing(null);
      toast.error(err?.data?.error || t('coupon_error', 'Failed to validate coupon'));
    }
  };

  const handleClearCoupon = () => {
    setShowRetryValues(false);
    setCouponCode("");
    setCouponPricing(null);
  };

  const getValidationSchema = (tab, step, domainStatus) => {
    if (tab !== 1) return yup.object({});
    
    switch (step) {
      case 1:
        return yup.object({
          firstName: yup.string().required(t('first_name_required')),
          lastName: yup.string().required(t('last_name_required')),
          email: yup
            .string()
            .email(t('email_invalid'))
            .required(t('email_required'))
            .test(
              "email-availability",
              t('email_already_exists'),
              () => emailStatus !== "taken"
            ),
          phone: yup
            .string()
            .required(t('phone_required'))
            .matches(/^[0-9+\-\s]+$/, t('phone_invalid')),
          password: yup
            .string()
            .required(t('password_required'))
            .min(8, t('password_min'))
            .matches(
              /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&^#()[\]{}\-_=+|;:'",.<>\/\\]).+$/,
              t('password_invalid')
            ),
        });
      case 2:
        return yup.object({
          companyName: yup.string().required(t('company_name_required')),
          city: yup.string().required(t('city_required')),
          address: yup.string().required(t('address_required')),
          country: yup.string().required(t('country_required')),
          timezone: yup.string().required(t('timezone_required')),
        });
      case 3:
        return yup.object({
          domain: yup
            .string()
            .required(t('domain_required'))
            .matches(/^[a-z0-9]+(-[a-z0-9]+)*$/, t('domain_invalid'))
            .min(3, t('min_length', { length: 3 }))
            .max(20, t('max_length', { length: 20 }))
            .test(
              "domain-availability",
              t('domain_not_available'),
              () => {
                // Return true if not currently marked as taken
                return domainStatus !== "taken";
              }
            ),
          brandColor: yup.string().required(t('brand_color_required')),
        });
      case 4:
        return yup.object({
          otp: yup.string().required(t('otp_required', 'OTP is required')).length(6, t('otp_length', 'OTP must be 6 digits')),
        });
      default:
        return yup.object({});
    }
  };

  const validationSchema = useMemo(() => getValidationSchema(currentTab, currentStep, domainStatus), [currentTab, currentStep, domainStatus, emailStatus]);

  const initialValues = useMemo(() => {

    const leadData = inviteTokenData?.data || {};
    const regData = registrationData || {};
    const detectedCountry = COUNTRY_OPTIONS.find(c => c.label.toLowerCase() === locationData?.country?.toLowerCase());
    const countryCode = locationData?.country?.toUpperCase();
    const defaultTimezone = countryCode ? (COUNTRY_TIMEZONE_MAP[countryCode] || "") : "";
    return {
      // SignUpStep - Step 1
      firstName: regData.first_name || leadData.first_name || "",
      lastName: regData.last_name || leadData.last_name || "",
      email: regData.email || leadData.email || "",
      phone: regData.phone_number || leadData.phone || "",
      countryCode: regData.country_code || leadData.country_code || detectedCountry?.value || COUNTRY_OPTIONS?.[0]?.value || "+965",
      password: "",
      couponCode: regData.coupon_code || "",

      // SignUpStep - Step 2
      companyName: regData.company_name || leadData.org_name || "",
      city: regData.city || "",
      address: regData.address || "",
      country: regData.country || detectedCountry?.label || COUNTRY_OPTIONS?.[0]?.label || "kw",
      timezone: regData.timezone || defaultTimezone,
      about: "",

      // SignUpStep - Step 3
      domain: regData.domain || "",
      brandColor: regData.brand_color || "#D90F0F",
      logo: null,

      // SignUpStep - Step 4
      otp: "",

      // Payment Step
      payment_provider: 'tap',
      payment_token: '',
    };
  }, [planSlug, inviteTokenData, registrationData, locationData]);
  
  // handle loading, error and not found
  if (isLoading || isLoadingRegistration || isLoadingInvite || isCurrencyLoading) {
    return <LoadingIndicator className={"full-width"}/>;
  }

  if (isError || isErrorRegistration || (inviteToken && (isErrorInvite || inviteTokenData?.success === false)) || (!inviteToken && (!subscriptionPlan || subscriptionPlan?.success === false))) {
    if (isErrorRegistration) {
      toast.error(t('payment_unexpected_error', 'Unable to resume payment. Please start again.'));
    }
    return <Navigate to="/" />;
  }

  const isFlatPricing = subscriptionPlan?.pricing_model === "flat";

  // Called directly from PaymentStep with the token - bypasses Formik values
  // to avoid the setFieldValue async race condition.
  const handlePaymentSubmit = async ({ payment_provider, payment_token }) => {
    try {
      const body = {
        payment: { payment_provider, payment_token },
      };
      const payment = await postRegistrationSubmit({ token: registrationToken, data: body }).unwrap();

      if (!payment?.action) {
        toast.error(t('payment_unexpected_error', 'An unexpected error occurred. Please try again.'));
        return;
      }

      if (payment.action === 'redirect') {
        setPaymentAction({ action: 'redirect', trackingToken: payment.tracking_token });
        window.location.href = payment.url;
        // Wait for the browser to naturally unload the page
        return new Promise(() => {}); 
      } else if (payment.action === 'wait') {
        setPaymentAction({ action: 'wait', trackingToken: payment.tracking_token });
        return true;
      }
    } catch (error) {
      const message =
        error?.data?.error ||
        error?.data?.message ||
        t('payment_failed', 'Payment submission failed. Please try again.');
      toast.error(message);
      throw error;
    }
  };
  return (
    <>
      {/* Payment polling overlay — shown after action: 'wait' response */}
      {paymentAction?.action === 'wait' && (
        <PaymentPollingScreen
          trackingToken={paymentAction.trackingToken}
          isFlatPricing={isFlatPricing}
          onSuccess={() => {
            console.log('Payment confirmed via webhook!');
          }}
          onError={(err) => {
            console.error('Polling error:', err);
            setPaymentAction(null);
          }}
        />
      )}

      <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize={true}
      validateOnMount={false}
      validateOnBlur={true}
      validateOnChange={true}
      onSubmit={() => {}}
    >
      {({ values, handleSubmit, setTouched }) => {
        let discount = 0;
        let regularPrice = 0;
        let totalPrice = 0;
        let basePrice = 0;
        let priceAfterTrial = 0;
        let applyDurationType = null;
        let applyCycles = null;
        let couponDetails = null;
        let unitPrice = subscriptionPlan?.price_per_seat ?? 0;
        const subscriptionDuration = subscriptionPlan?.cadence || inviteTokenData?.data?.cadence;
        // Resolve trial days - coupon overrides plan, dynamicPricing.discount overrides both (When Dynamic Pricing is enabled coupon are disabled)
        const planTrialDays = subscriptionPlan?.trial_days || inviteTokenData?.data?.trial_days || registrationData?.trial_days || 0;
        const couponTrialDays = couponPricing?.discount?.trial_days || 0;
        const dynamicTrialDays = dynamicPricing?.discount?.trial_days || 0;
        const trialDays = dynamicTrialDays || couponTrialDays || planTrialDays;
        const isTrial = trialDays > 0;
        
        if (subscriptionPlan?.plan_type === CUSTOM_PLAN && dynamicPricing && !showRetryValues) {
          regularPrice = dynamicPricing.regular_price;
          discount = dynamicPricing.discount_amount || 0;
          totalPrice = isTrial ? 0 : dynamicPricing.total_price;
          basePrice = dynamicPricing?.base_price;
          unitPrice = dynamicPricing?.price_per_seat;
          priceAfterTrial = dynamicPricing.total_price;
          applyDurationType = dynamicPricing.discount?.apply_duration_type;
          applyCycles = dynamicPricing.discount?.apply_cycles;
        } else if (couponPricing) {
          regularPrice = couponPricing.regular_price;
          discount = couponPricing.discount_amount || 0;
          totalPrice = isTrial ? 0 : couponPricing.total_price;
          basePrice = couponPricing.base_price;
          unitPrice = couponPricing.price_per_seat;
          priceAfterTrial = couponPricing.total_price;
          applyDurationType = couponPricing.discount?.apply_duration_type;
          applyCycles = couponPricing.discount?.apply_cycles;
          couponDetails = {
            discount_type: couponPricing.discount?.discount_type,
            discount_value: couponPricing.discount?.discount_value,
            max_coupon_value: couponPricing.discount?.max_coupon_value
          };
        } else if (inviteTokenData?.data) {
          discount = inviteTokenData.data.discount_amount || 0;
          totalPrice = inviteTokenData.data.total_price || 0;
          basePrice = inviteTokenData.data.base_price || 0;
          unitPrice = inviteTokenData.data.price_per_seat || 0;
          regularPrice = (Number(discount) + Number(totalPrice));
          totalPrice = isTrial ? 0 : totalPrice;
          priceAfterTrial = inviteTokenData.data.total_price || 0;
          applyDurationType = inviteTokenData.data.discount_duration_type;
          applyCycles = inviteTokenData.data.discount_allowed_cycles;
        } else if (registrationData?.order && showRetryValues) {
          const order = registrationData.order;
          discount = (Number(order.discount_amount) || 0) + (Number(order.coupon_amount) || 0);
          totalPrice = isTrial ? 0 : order.total_amount;
          basePrice = order.base_price;
          unitPrice = order.price_per_seat;
          regularPrice = (Number(discount) + Number(order.total_amount));
          priceAfterTrial = order.base_amount - discount;
          applyDurationType = order.coupon_duration_type || order.discount_duration_type;
          applyCycles = order.coupon_allowed_cycles || order.discount_allowed_cycles;
        } else {
          regularPrice = subscriptionPlan?.pricing_model === "flat" ? subscriptionPlan?.base_price : quantity * unitPrice;
          regularPrice = Math.max(0, regularPrice - discount);
          totalPrice = isTrial ? 0 : regularPrice;
          priceAfterTrial = regularPrice;
        }

        regularPrice = Number(regularPrice);
        totalPrice = Number(totalPrice);
        const handleTabChange = (targetTab, setTouched) => {
          const resolvedTab = typeof targetTab === "function" ? targetTab(currentTab) : targetTab;
          
          const isInvite = !!inviteToken || registrationData?.is_invite_token;
          if (isInvite && resolvedTab === 0) return;

          if (resolvedTab === 1) {
            setTouched({}, false);
            setCurrentStep(1);
          }

          // If moving forward from Basket Step, clear coupon if not applied
          if (currentTab === 0 && resolvedTab > 0 && !couponPricing) {
            setCouponCode("");
          }

          setCurrentTab(resolvedTab);
        };
        const showPaypal = subscriptionPlan?.paypal_plan_id && subscriptionPlan?.paypal_plan_id !== "";
        const isInvite = !!inviteToken || registrationData?.is_invite_token;
        const canGoBack = currentTab !== 0 && (!inviteToken || currentTab > 1) && (!isInvite || currentTab > 1);

        return (
          <Form>
            {/* Domain availability watcher */}
            <FieldAvailabilityWatcher
              fieldName="domain"
              setStatus={setDomainStatus}
              triggerCheck={triggerCheckDomain}
              shouldSkip={(v) => !v || v.length < 3 || !/^[a-zA-Z0-9-]+$/.test(v)}
              errorMessage={t('domain_not_available')}
            />
            {/* Email uniqueness watcher */}
            <FieldAvailabilityWatcher
              fieldName="email"
              setStatus={setEmailStatus}
              triggerCheck={triggerCheckEmail}
              shouldSkip={(v) => !v || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)}
              errorMessage={t('email_already_exists')}
              debounceMs={700}
            />
            <div className="auth-wrapper">
              <div className="auth-header">
                <div className="cs-container">
                  <Link to="/" className="left">
                    <img src={"/images/business-logo.svg"} alt="" />
                  </Link>
                  {canGoBack && (
                    <div className="right">
                      <button type="button" onClick={() => handleTabChange(currentTab > 0 ? currentTab - 1 : currentTab, setTouched)}>
                        <LeftArrowLine />
                        <p>{t('back')}</p>
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="steps-wrapper login">
                <div className="steps-container">
                  <StepTabs 
                    tabContent={[
                      ...((inviteToken || registrationData?.is_invite_token) ? [] : [{className: "step", name: t('tab_basket')}]),
                      {className: "step", name: t('tab_auth')},
                      {className: "step", name: t('tab_payment')},
                    ]}
                    currentTab={(inviteToken || registrationData?.is_invite_token) ? currentTab - 1 : currentTab}
                    mainClass={"steps"}
                  />
                  <div className="auth-content">
                    <div className="cs-container">
                      <div className="auth-inner-wrapper">
                        {currentTab === 0 && (
                          <BasketStep
                            subscriptionPlan={subscriptionPlan}
                            quantity={quantity}
                            setQuantity={setQuantity}
                            setShowRetryValues={setShowRetryValues}
                            pricingData={{
                              totalPrice,
                              discount,
                              regularPrice,
                              basePrice,
                              unitPrice,
                              isTrial,
                              trialDays,
                              priceAfterTrial,
                              subscriptionDuration,
                              applyDurationType,
                              applyCycles,
                              couponDetails
                            }}
                            isFlatPricing={isFlatPricing}
                            setCurrentTab={(newTab) => handleTabChange(newTab, setTouched)}
                            couponCode={couponCode}
                            setCouponCode={setCouponCode}
                            onApplyCoupon={handleApplyCoupon}
                            onClearCoupon={handleClearCoupon}
                            isCouponApplied={!!couponPricing}
                            openDemoModal={() => setOpenDemoModal(true)}
                          />
                        )}
                        {currentTab === 1 && (
                          <SignUpStep 
                            setCurrentTab={(newTab) => handleTabChange(newTab, setTouched)}
                            currentStep={currentStep}
                            setCurrentStep={setCurrentStep}
                            registrationToken={registrationToken}
                            setRegistrationToken={setRegistrationToken}
                            subscriptionPlan={subscriptionPlan}
                            isFlatPricing={isFlatPricing}
                            quantity={quantity}
                            triggerCheckDomain={triggerCheckDomain}
                            inviteTokenData={inviteTokenData?.data}
                            inviteToken={inviteToken}
                            couponCode={couponCode}
                              pricingData={{
                                totalPrice,
                                discount,
                                regularPrice,
                                basePrice,
                                unitPrice,
                                isTrial,
                                trialDays,
                                priceAfterTrial,
                                subscriptionDuration,
                                applyDurationType,
                                applyCycles
                              }}
                            />
                          )}
                      </div>
                    </div>
                  </div>
                  {currentTab === 2 && (
                    <div className="auth-inner-wrapper">
                    <PaymentStep 
                      quantity={quantity}
                      pricingData={{
                        totalPrice,
                        discount,
                        regularPrice,
                        basePrice,
                        unitPrice,
                        isTrial,
                        trialDays,
                        priceAfterTrial,
                        subscriptionDuration,
                        applyDurationType,
                        applyCycles,
                        couponDetails
                      }}
                      isPosting={isPosting}
                      onSubmitPayment={handlePaymentSubmit}
                      showPaypal={showPaypal}
                      subscriptionPlan={subscriptionPlan}
                      isInviteFlow={inviteToken ? true : false}
                      isFlatPricing={isFlatPricing}
                    />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
    { openDemoModal && <RequestDemoModal isOpened={openDemoModal} onClose={() => setOpenDemoModal(false)} planId={subscriptionPlan?.id} />}
    </>
  );
};

export default Signup;
